import { rem } from 'polished'
import { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Heading from 'LuxKit/Typography/Heading'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import RadioButton from 'LuxKit/Radio/RadioButton'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'
import { floatify } from 'lib/maths/mathUtils'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useLuxPlusPricingContext from 'hooks/useLuxPlusPricingContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import LoyaltyTierBenefitAvailabilityMessageBanner from 'loyalty/components/LoyaltyTierBenefitAvailabilityMessageBanner'

import {
  getPropertyRatePayablePrice,
  type PropertyRateUpgradeCandidate,
  type PropertyRateUpgradeVariant,
} from 'components/OfferPage/offerPageUtils/propertyLoyaltyPackageUpgrade'

import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyRateInclusions from './PropertyRateInclusions'

const UpgradeSheet = styled(Sheet)`
  padding: ${rem(20)};
`

const TierAccentText = styled(BodyText)`
  ${tierDarkForegroundColorStyle}
`

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${rem(12)};
`

// Only the browser default margins are cleared; the top gap comes from the enclosing VerticalSpacer,
// so a blanket `margin: 0` would flatten the rule against whatever sits above it.
const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin-block-end: 0;
  margin-inline: 0;
`

export interface PropertyLoyaltyPackageUpgradeModalResult {
  action: 'apply' | 'remove'
  /** The refundability option the customer chose. Absent when removing. */
  sourceRate?: App.EnrichedPropertyRate
  targetRate?: App.EnrichedPropertyRate
}

interface Props {
  candidate: PropertyRateUpgradeCandidate
  /** Needed to render the target rate's inclusions the way the rate tile does. */
  property: App.Property.Property
  /**
   * The source variant currently applied, so reopening the modal shows what the customer picked
   * rather than resetting to the cheapest option. Absent when no upgrade is applied — which is also
   * what tells the modal whether to offer Apply or Remove for the selected option.
   */
  appliedSourceRateId?: string
  propertyTimeZone: string
}

function PropertyLoyaltyPackageUpgradeModal({ candidate, property, appliedSourceRateId, propertyTimeZone }: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext<PropertyLoyaltyPackageUpgradeModalResult | undefined>()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const upgradeBenefit = useLoyaltyUserBenefitView('hotel_upgrade')
  const pricingContext = useLuxPlusPricingContext()

  const [selectedRateId, setSelectedRateId] = useState(appliedSourceRateId ?? candidate.sourceRate.rateId)

  const selectedVariant = useMemo(
    () => candidate.variants.find(variant => variant.rate.rateId === selectedRateId) ?? candidate.variants.find(variant => variant.selectable),
    [candidate.variants, selectedRateId],
  )

  const onSelectVariant = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRateId(event.currentTarget.value)
  }, [])

  const onApply = useCallback(() => {
    if (!selectedVariant?.target) return
    resolve({ action: 'apply', sourceRate: selectedVariant.rate, targetRate: selectedVariant.target })
  }, [resolve, selectedVariant])

  const onRemove = useCallback(() => {
    resolve({ action: 'remove' })
  }, [resolve])

  const onClose = useCallback(() => {
    resolve(undefined)
  }, [resolve])

  // Mobile scopes the modal to the single rate behind the card it was opened from, so there is nothing
  // to choose between. A one-option radio group reads as a broken control, so the option becomes plain
  // text and only a genuine choice keeps its radios.
  const hasRefundabilityChoice = candidate.variants.length > 1

  // The upgrade belongs to whichever option is selected, so picking a different one offers to move it
  // there rather than to take it off. Only the option actually holding the upgrade offers Remove.
  const isSelectedVariantApplied = !!appliedSourceRateId && selectedVariant?.rate.rateId === appliedSourceRateId

  const isUnlimited = upgradeBenefit?.availability === Infinity
  const tierDisplayName = getLoyaltyTierDisplayName(tier)
  const tierClassName = `tier-${tier}`
  const targetRate = selectedVariant?.target ?? candidate.targetRate

  // The tier is named with the program in front of it, matching the room list's upgrade filter chip.
  // Infinite is the exception: it carries its own branding and is never prefixed with the program
  // name. Only infinite receives unlimited upgrades, so the unlimited copy below needs no such
  // branch — it can only ever describe an infinite member.
  const isInfiniteTier = tier === 'infinite'
  const programDisplayName = getLoyaltyProgramDisplayName()

  let modalTitle: string
  if (isInfiniteTier) {
    modalTitle = intl.formatMessage(
      {
        id: 'XHvWdfv_aJIincpORhEn',
        defaultMessage: 'Free Upgrades with {tierName}',
        description: 'Title of the offer page modal where a loyalty member applies their free package upgrade',
      },
      { tierName: tierDisplayName },
    )
  } else {
    modalTitle = intl.formatMessage(
      {
        id: 'UUi2MFSMrTusAI-pFZAB',
        defaultMessage: 'Free Upgrades with {program} {tierName}',
        description: 'Title of the offer page modal where a loyalty member of a named program tier applies their free package upgrade',
      },
      { program: programDisplayName, tierName: tierDisplayName },
    )
  }

  let allowanceMessage: string
  if (isInfiniteTier) {
    allowanceMessage = intl.formatMessage(
      {
        id: '4rAwxni7KBEEz_FBVcg0',
        defaultMessage: 'You receive {count, plural, one {# free upgrade} other {# free upgrades}} each membership year as an {tierName} member.',
        description: 'Explains how many free package upgrades a loyalty member receives each membership year',
      },
      { count: upgradeBenefit?.benefit.limit ?? 0, tierName: tierDisplayName },
    )
  } else {
    allowanceMessage = intl.formatMessage(
      {
        id: 'NxOCU8ye-ngyl2xOy_S9',
        defaultMessage: 'You receive {count, plural, one {# free upgrade} other {# free upgrades}} each membership year as a {program} {tierName} member.',
        description: 'Explains how many free package upgrades a loyalty member of a named program tier receives each membership year',
      },
      { count: upgradeBenefit?.benefit.limit ?? 0, program: programDisplayName, tierName: tierDisplayName },
    )
  }

  // Both figures are what the customer would actually be charged, at the selected refundability —
  // so the gap between them is exactly the value of the free upgrade.
  // Deltas on each option are measured against the package's cheapest option, matching how the rate
  // row shows "+$X" beside its dearer refundability choice.
  const basePrice = Math.min(...candidate.variants.map(variant => getPropertyRatePayablePrice(variant.rate, pricingContext)))
  const yourPrice = selectedVariant ? getPropertyRatePayablePrice(selectedVariant.rate, pricingContext) : undefined
  const standardPrice = selectedVariant?.target
    ? getPropertyRatePayablePrice(selectedVariant.target, pricingContext)
    : undefined

  return <ModalBase size="S">
    <ModalHeader
      title={modalTitle}
      onCloseButtonClick={onClose}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24}>
          {!isUnlimited && <BodyText variant="large" colour="neutral-one">
            {allowanceMessage}
          </BodyText>}
          {isUnlimited && <BodyText variant="large" colour="neutral-one">
            {intl.formatMessage(
              {
                id: 'XmECy5UNaZybrAepkNWN',
                defaultMessage: 'You receive unlimited free upgrades as an {tierName} member.',
                description: 'Explains that a top tier loyalty member receives unlimited free package upgrades',
              },
              { tierName: tierDisplayName },
            )}
          </BodyText>}
          <LoyaltyTierBenefitAvailabilityMessageBanner
            benefitType="hotel_upgrade"
            availabilityDisplay="upgrades-remaining"
            icon={<LoyaltyTierIcon tier={tier} />}
            iconSurface={false}
            flat
          />

          <BodyText variant="large" colour="neutral-one">
            {intl.formatMessage({
              id: 'zqa7Gwt_G-htJAmTNyIf',
              defaultMessage: 'Below are your available upgrades:',
              description: 'Lead-in above the list of package upgrades a loyalty member can apply to their booking',
            })}
          </BodyText>

          <UpgradeSheet border="light-foreground" borderRadius="XL">
            <VerticalSpacer gap={20}>
              <VerticalSpacer gap={4}>
                <TierAccentText className={tierClassName} variant="large" weight="bold">
                  {intl.formatMessage({
                    id: 'rO4MOHLLqbSwdlNywxm0',
                    defaultMessage: 'Upgrade your package to:',
                    description: 'Label above the name of the package a loyalty member would be upgraded to',
                  })}
                </TierAccentText>
                <Heading variant="heading5">{targetRate.name}</Heading>
              </VerticalSpacer>

              <Group direction="vertical" gap={8}>
                {candidate.variants.map(variant => <UpgradeVariantRow
                  key={variant.rate.rateId}
                  variant={variant}
                  checked={variant.rate.rateId === selectedVariant?.rate.rateId}
                  onChange={onSelectVariant}
                  pricingContext={pricingContext}
                  isAppliedVariant={variant.rate.rateId === appliedSourceRateId}
                  propertyTimeZone={propertyTimeZone}
                  basePrice={basePrice}
                  showRadio={hasRefundabilityChoice}
                />)}
              </Group>

              {/* The whole inclusion set, not just the plain list — the LuxPlus+ and bonus
                  inclusions are part of what the upgrade gives, and the rate tile shows them.
                  Same heading and string the applied rate tile uses, so the two read alike. */}
              <VerticalSpacer gap={4}>
                <TierAccentText className={tierClassName} variant="small" weight="bold">
                  {intl.formatMessage({
                    id: 'HRYT9P5ypghRMebUdoEQ',
                    defaultMessage: 'Your upgraded inclusions',
                    description: 'Heading above the inclusions of the package a loyalty member has upgraded to on the offer page rate tile',
                  })}
                </TierAccentText>
                <PropertyRateInclusions
                  maxHeight={208}
                  property={property}
                  inclusions={targetRate.inclusions}
                  bonusInclusions={targetRate.bonusInclusions}
                  luxPlusInclusions={targetRate.luxPlusInclusions}
                  bundleInclusions={targetRate.bundleInclusions}
                />
              </VerticalSpacer>

              <Divider />

              {standardPrice !== undefined && <PriceRow>
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: 'OI4Hevo2SoLzBjrt0QTj',
                    defaultMessage: 'Standard price for this package',
                    description: 'Label for what the upgraded package would normally cost without the loyalty benefit',
                  })}
                </BodyText>
                <BodyText variant="medium">
                  <FormattedMessage
                    id="1nAjzdEJRwMgJugEo-uq"
                    defaultMessage="From {amount} total"
                    description="Total price of a package shown in the loyalty free upgrade modal on the offer page"
                    values={{ amount: <FormatCurrency value={standardPrice} format="roundedDollar" /> }}
                  />
                </BodyText>
              </PriceRow>}

              <Divider />

              {yourPrice !== undefined && <PriceRow>
                <TierAccentText className={tierClassName} variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: '-cpUR-6aXBZ696wy_zoN',
                    defaultMessage: 'Your price with a free upgrade',
                    description: 'Label for what the loyalty member pays once their free package upgrade is applied',
                  })}
                </TierAccentText>
                <TierAccentText className={tierClassName} variant="medium" weight="bold">
                  <FormattedMessage
                    id="1nAjzdEJRwMgJugEo-uq"
                    defaultMessage="From {amount} total"
                    description="Total price of a package shown in the loyalty free upgrade modal on the offer page"
                    values={{ amount: <FormatCurrency value={yourPrice} format="roundedDollar" /> }}
                  />
                </TierAccentText>
              </PriceRow>}

              <ProductPaletteProvider palette={`tier-${tier}`}>
                {!isSelectedVariantApplied && <TextButton
                  kind="primary"
                  size="medium"
                  fit="full-width"
                  disabled={!selectedVariant?.target}
                  onClick={onApply}
                >
                  {intl.formatMessage({
                    id: 'nFUlKRjNPl3zktvNdpGE',
                    defaultMessage: 'Apply Free Upgrade',
                    description: 'Button that applies the loyalty member free package upgrade to their booking',
                  })}
                </TextButton>}
                {isSelectedVariantApplied && <TextButton
                  kind="secondary"
                  size="medium"
                  fit="full-width"
                  onClick={onRemove}
                >
                  {intl.formatMessage({
                    id: 'DNEi-PabIAJMb8h26EUL',
                    defaultMessage: 'Remove upgrade',
                    description: 'Button that removes the loyalty member free package upgrade from their booking',
                  })}
                </TextButton>}
              </ProductPaletteProvider>
            </VerticalSpacer>
          </UpgradeSheet>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

interface VariantRowProps {
  variant: PropertyRateUpgradeVariant
  checked: boolean
  /** True only for the option currently holding the upgrade, whose payable figure already includes it. */
  isAppliedVariant: boolean
  pricingContext: ReturnType<typeof useLuxPlusPricingContext>
  propertyTimeZone: string
  /** The package's cheapest option, which the surcharge on dearer options is measured against. */
  basePrice: number
  /** False when this is the only option, so there is no choice for a radio to represent. */
  showRadio: boolean
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * One refundability option. Options the upgrade isn't offered on stay visible but disabled, so the
 * customer can see the trade-off rather than wondering where an option went.
 */
function UpgradeVariantRow({ variant, checked, isAppliedVariant, pricingContext, propertyTimeZone, basePrice, showRadio, onChange }: VariantRowProps) {
  const intl = useIntl()
  const { rate, selectable } = variant

  // Reads as part of the option's own label at normal contrast, the same way the rate row words it —
  // only the radio control looks disabled. No remove link here; the modal has its own Remove button.
  const unavailableLabel = intl.formatMessage({
    id: 'ySx1CdWOvmrVkSOIQbcR',
    defaultMessage: '(Unavailable with free upgrade)',
    description: 'Note beside a cancellation option that the loyalty free package upgrade cannot be applied to',
  })

  // `PropertyCancellationPolicy` renders the same wording, colour and info tooltip the rate row uses
  // ("Free cancellation until Sun, 4 Jan"), so the two surfaces can't drift apart.
  const surcharge = floatify(getPropertyRatePayablePrice(rate, pricingContext) - basePrice)

  return <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap">
    {showRadio && <RadioButton
      value={rate.rateId}
      checked={checked}
      disabled={!selectable}
      onChange={onChange}
    />}
    <PropertyCancellationPolicy
      propertyTimeZone={propertyTimeZone}
      headlineCancellationPolicyType={rate.headlineCancellationPolicyType}
      headlineCancellationPolicyStart={rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined}
      fullyRefundableUntil={rate.fullyRefundableUntil}
      cancellationPolicies={rate.cancellationPolicies}
      currency={rate.currency}
      shouldClarifyRefundIsHotelOnly={rate.includesFlights}
    />
    {/* Hidden on the option holding the upgrade, whose payable figure already includes this step up.
        The others keep it, so the cost of switching to them stays visible. */}
    {selectable && !isAppliedVariant && surcharge > 0 && <BodyText variant="medium" weight="bold">
      <FormattedMessage
        id="C8U8iKpKHQq1SdIL8Kp7"
        defaultMessage="+{amount}"
        description="Extra cost of choosing a more flexible cancellation option in the free package upgrade modal"
        values={{ amount: <FormatCurrency value={surcharge} format="roundedDollar" /> }}
      />
    </BodyText>}
    {!selectable && <BodyText variant="medium" colour="normal-contrast">
      {unavailableLabel}
    </BodyText>}
  </Group>
}

export default PropertyLoyaltyPackageUpgradeModal
